import { useState, useRef } from 'react';
import { useTimer } from '../../contexts/TimerContext';
import { useChild } from '../../contexts/ChildContext';
import { useAuth } from '../../contexts/AuthContext';
import { logTimerEntry } from '../../utils/timerLogger';
import { getAgeWeeks, getWakeWindow } from '../../utils/wakeWindowUtils';
import SleepInterruptionModal from './SleepInterruptionModal';

function formatElapsed(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${String(s).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${String(rm).padStart(2, '0')}m`;
}

const TIMER_CONFIG = {
  sleep:         { icon: '😴', label: 'Sleep' },
  breastfeeding: { icon: '🤱', label: 'Breastfeeding' },
};

/**
 * ActiveTimerBanner — floating pill visible from every page.
 *
 * "Stop & Log" directly submits the entry to Firestore without navigation.
 * For sleep timers, a "Wake-up" button opens SleepInterruptionModal to log
 * disturbances mid-session. Wake-ups are stored on the Firestore timer doc
 * and included in the sleep entry when the timer is stopped.
 */
export default function ActiveTimerBanner() {
  const { timer, elapsed, stop, cancel, addWakeUp } = useTimer();
  const { selectedChild }                           = useChild();
  const { user, profile }                           = useAuth();

  const [status, setStatus]               = useState('idle');
  const [snapshot, setSnapshot]           = useState(null);
  const [interruptionOpen, setInterrupt]  = useState(false);
  const dismissTimerRef                   = useRef(null);

  if (!timer && status === 'idle') return null;

  const config = TIMER_CONFIG[timer?.type || snapshot?.type] || { icon: '⏱', label: 'Timer' };
  const wakeUps      = timer?.wakeUps || [];
  const wakeUpCount  = wakeUps.length;
  const isSleepTimer = timer?.type === 'sleep';

  const handleStopAndLog = async () => {
    const result = stop();
    if (!result) return;

    setSnapshot({ type: result.type, elapsed: result.elapsedSeconds });
    setStatus('logging');

    try {
      await logTimerEntry(result, selectedChild?.id, user, profile);
      setStatus('success');
    } catch (err) {
      console.error('Timer auto-log failed:', err);
      setStatus('error');
    }

    clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => {
      setStatus('idle');
      setSnapshot(null);
    }, status === 'error' ? 4000 : 2500);
  };

  const handleCancel = () => {
    cancel();
    setStatus('idle');
    setSnapshot(null);
  };

  const handleWakeUp = (wakeUpEntry) => {
    addWakeUp(wakeUpEntry);
  };

  // ---- Status states -------------------------------------------------
  if (status === 'logging') {
    return (
      <div className="active-timer-banner active-timer-banner--muted" aria-live="polite">
        <span className="timer-banner-icon" aria-hidden="true">{config.icon}</span>
        <span className="timer-banner-label">Logging…</span>
        <div className="spinner-border spinner-border-sm text-white ms-1" role="status" style={{ width: 14, height: 14, borderWidth: 2 }}>
          <span className="visually-hidden">Saving…</span>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="active-timer-banner active-timer-banner--success" aria-live="polite">
        <span aria-hidden="true">✓</span>
        <span className="timer-banner-label">{config.label} logged</span>
        {snapshot?.elapsed > 0 && (
          <span className="timer-banner-elapsed">{formatElapsed(snapshot.elapsed)}</span>
        )}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="active-timer-banner active-timer-banner--error" aria-live="assertive">
        <span aria-hidden="true">⚠</span>
        <span className="timer-banner-label">Log failed — try again</span>
      </div>
    );
  }

  // ---- Wake window over-window detection (sleep timer only) ----------
  const elapsedMin    = Math.floor(elapsed / 60);
  const ageWeeks      = getAgeWeeks(selectedChild?.dateOfBirth);
  const sleepWindow   = isSleepTimer ? getWakeWindow(ageWeeks) : null;
  const overWindowMin = sleepWindow && elapsedMin > sleepWindow.maxMin
    ? elapsedMin - sleepWindow.maxMin
    : 0;
  const isOverWindow  = overWindowMin > 0;

  // ---- Running state -------------------------------------------------
  return (
    <>
      <div
        className={`active-timer-banner${isOverWindow ? ' active-timer-banner--warning' : ''}`}
        role="status"
        aria-live="polite"
      >
        <span className="timer-banner-dot" aria-hidden="true" />
        <span className="timer-banner-icon" aria-hidden="true">{config.icon}</span>
        <span className="timer-banner-label">
          {config.label}
          {isOverWindow && (
            <span className="timer-banner-over-window"> · ⚠ over window</span>
          )}
        </span>

        {/* Wake-up count badge — sleep only */}
        {isSleepTimer && wakeUpCount > 0 && (
          <span className="timer-banner-wakeup-count" title={`${wakeUpCount} wake-up${wakeUpCount !== 1 ? 's' : ''} logged`}>
            💤 {wakeUpCount}
          </span>
        )}

        <span className="timer-banner-elapsed">{formatElapsed(elapsed)}</span>

        <div className="timer-banner-actions">
          <button
            className="timer-banner-cancel"
            onClick={handleCancel}
            aria-label={`Cancel ${config.label} timer`}
            title="Cancel timer"
          >✕</button>

          {/* Wake-up button — sleep timers only */}
          {isSleepTimer && (
            <button
              className="timer-banner-wakeup-btn"
              onClick={() => setInterrupt(true)}
              title="Log a wake-up"
            >
              + Wake-up
            </button>
          )}

          <button
            className={`timer-banner-stop${isOverWindow ? ' timer-banner-stop--warning' : ''}`}
            onClick={handleStopAndLog}
          >
            Stop &amp; Log
          </button>
        </div>
      </div>

      {/* Sleep interruption modal */}
      {isSleepTimer && (
        <SleepInterruptionModal
          isOpen={interruptionOpen}
          onClose={() => setInterrupt(false)}
          onConfirm={handleWakeUp}
          childId={selectedChild?.id}
        />
      )}
    </>
  );
}
